import {
  APP_COMPLETION_STATUS,
  APP_SECTION,
  APPLICATION,
  PAGES,
  PARTY,
  PARTY_RELATIONSHIPS,
  PRINCIPAL,
  STORAGE_KEYS,
  getReciprocalRelationshipFor,
  OWNER_TYPE,
  ENTITY_TYPE,
  createPrimaryBeneficiaryFromParty,
  BENEFICIARY,
  BENEFICIARY_SURVIVOR_UID,
  createPrimaryBeneficiary,
  BENEFICIARY_SURVIVOR_FIRST_NAME, BENEFICIARY_SURVIVOR_LAST_NAME, BENEFICIARY_TYPE, ENABLE_FLORIDA_FLOW
} from "./constants";
import {isValid, isEqual, mygaSession} from "./myga-session";
import {CONDITION_NAMES, CONDITIONS, VALIDATION_REPORT} from "./nfg-myga";
import {NassauLog} from "./myga-utils";
import * as _ from 'underscore';
import {getPageFlowFromProductType, makePayment} from "./product-manager";
import {MM_PAYMENT, MM_PAYMENT_TYPE, MM_PRODUCT_DTO} from "./myga-model";

const getNamedCondition = (name) => {
  // 2021.09.22.ADivelbiss - the condition should be an exact match NOT a contains.
  let conditions = CONDITIONS.filter(c=>(c.name===name));
  // let conditions = CONDITIONS.filter(c=>(c.name.indexOf(name)>=0));
  return (conditions.length>0)?conditions[0].condition:null;
}

const isFunction = (obj) => {
  return (typeof obj === 'function') || (obj instanceof Function);
};

const conditionPredicate = (c) => {
  return isValid(c.operator)||
      isValid(c.name)||
      isValid(c.function)||
      isValid(c.AND)||
      isValid(c.OR)||
      isValid(c.FOR_ALL)||
      isValid(c.FOR_ANY)||
      isValid(c.page);
}

// This function handles parsing a section obj/value
const selectSectionData = (section, data) => {
  if (_.isObject(section)) {
    return data[section.name]
  }
  return isValid(data[section]) ? data[section] : data;
}

// This function returns a selection of data as defined by a condition
// If no selection can be made, all of data is returned
const selectData = (condition, data) => {
  if (!isValid(data)) return null;

  let selectedAppData = data;
  let section;

  // Check for a context definition within a condition
  if (isValid(condition.context) && isValid(selectedAppData[condition.context])) {
    selectedAppData = selectedAppData[condition.context];
  }

  // Check for a section definition within a condition
  if (isValid(condition.section)) {
    section = condition.section;
  } else {
    // Find a section within any other valid condition type
    // let valArray = _.values(condition).filter(v => _.has(v, 'section'))
    // if (valArray.length > 0) section = valArray[0].section;
  }

  // Check for a valid section, then select it from data
  if (isValid(section)) {
    selectedAppData = selectSectionData(section, selectedAppData);
  }

  // Handle selectedData arrays
  if (_.isArray(selectedAppData)) {
    if (isValid(section) && isValid(section.filter)) {
      selectedAppData = selectedAppData.filter(d => isConditionValid(section.filter, d))
    }
  }

  return selectedAppData;
}

// Sum the data of an operand.SUM
const handleOperandSUM = (operand, data) => {
  let total = 0;
  let sumData;
  operand.SUM.forEach(condition => {
    sumData = selectData(condition, data);
    // Handle arrays
    if (_.isArray(sumData)) {
      // Sum all values in array
      sumData.forEach(d => {
        if (isValid(d[condition.key])) {
          total += parseFloat(d[condition.key]);
        }
      })
    }
    // Handle non-array
    else if (isValid(sumData) && isValid(sumData[condition.key])) {
      total += parseFloat(sumData[condition.key]);
    }
  });
  return total;
}

// Return the length of an array within operand.LENGTH
const handleOperandLENGTH = (operand, data) => {
  const lenData = selectData(operand.LENGTH, data);
  if (_.isArray(lenData)) return lenData.length;
  return 0;
}

// Handle a condition operand
const handleOperand = (operand, data, _stack) => {
  if (!isValid(operand)) return null;

  data = selectData(operand, data);

  // Handle SUM
  if (isValid(operand.SUM)) {
    return handleOperandSUM(operand, data);
  }

  // Handle LENGTH
  if (isValid(operand.LENGTH)) {
    return handleOperandLENGTH(operand, data);
  }

  // Handle Count TODO: Do we remove this support in favor of something similar to SUM?
  if (isValid(operand.function) && _.isArray(data) && isValid(operand.condition)) {
    switch (operand.function) {
      case 'COUNT': // count the number of entries where the given condition is true
        return data.reduce((pv,obj)=>(pv+isConditionValid(operand.condition, obj, _stack)?1:0),0);
      default:
        return '';
    }
  }

  // Handle key
  if (isValid(operand.key)) {
    // Handle scale
    if (isValid(operand.scale)) {
      return data[operand.key] * operand.scale;
    }
    return data[operand.key];
  }
  return null;
}

// Determine the result of operandLeft, operandRight, and an operator
const evaluateOperands = (operandLeft, operandRight, operator) => {
  const x = Number.parseFloat(operandLeft);
  const y = Number.parseFloat(operandRight);
  switch (operator) {
    case '==':
      return (operandLeft === operandRight);
    case '!=':
      return (operandLeft !== operandRight);
    case '>':
      return (Number.isNaN(x) !== Number.isNaN(y))
          ? false // operands do not have the same number state
          : Number.isNaN(x)
              ? (operandLeft > operandRight) // possible string comparison
              : (x > y); // numeric comparison
    case '>=':
      return (Number.isNaN(x) !== Number.isNaN(y))
          ? false // operands do not have the same number state
          : Number.isNaN(x)
              ? (operandLeft >= operandRight) // possible string comparison
              : (x >= y); // numeric comparison
    case '<':
      return (Number.isNaN(x) !== Number.isNaN(y))
          ? false // operands do not have the same number state
          : Number.isNaN(x)
              ? (operandLeft < operandRight) // possible string comparison
              : (x < y); // numeric comparison
    case '<=':
      return (Number.isNaN(x) !== Number.isNaN(y))
          ? false // operands do not have the same number state
          : Number.isNaN(x)
              ? (operandLeft <= operandRight) // possible string comparison
              : (x <= y); // numeric comparison
    case '!':
      return (!operandRight);
    case 'isValid':
      return isValid(operandLeft) === operandRight;
    default:
      return false;
  }
}

// Recursively calls isConditionValid and breaks on isConditionValid === returnAtFirst
// Can evaluate a single condition multiple times against an array of data
// or an array of conditions against a single data item
// or an array conditions against an array of data
const processConditions = (conditions, data, returnAtFirst, _stack) => {
  // Handle a single condition given an array of data
  if (!_.isArray(conditions) && _.isArray(data)) {
    for (let i=0; i<data.length; i++) {
      if (isConditionValid(conditions, data[i], _stack) === returnAtFirst) return returnAtFirst;
    }
  }
  // Handle array of conditions given a single selection of data
  else if (_.isArray(conditions) && !_.isArray(data)) {
    for (let i=0; i<conditions.length; i++) {
      if (isConditionValid(conditions[i], data, _stack) === returnAtFirst) return returnAtFirst;
    }
  }
  // Handle array of conditions given an array of data
  else if (_.isArray(conditions) && _.isArray(data)) {
    for (let i=0; i<conditions.length; i++) {
      for (let x=0; x<data.length; x++) {
        if (isConditionValid(conditions[i], data[x], _stack) === returnAtFirst) return returnAtFirst;
      }
    }
  }
  return !returnAtFirst;
}

// Helper function to default returnAtFirst to false
const andConditionIsValid = (conditions, data, _stack) => {
  return processConditions(conditions, data, false, _stack);
}

// Helper function to default returnAtFirst to true
const orConditionIsValid = (conditions, data, _stack) => {
  return processConditions(conditions, data, true, _stack);
}

// Returns the left operand given a condition and data
const findLeftOperand = (condition, data) => {
  if (isValid(condition.operandLeft)) {
    return handleOperand(condition.operandLeft, data);
  }

  if (isValid(condition.question) && isValid(condition.section)) {
    return data[condition.question];
  }

  if (isValid(condition.key)) {
    return data[condition.key];
  }

  NassauLog(`missing operandLeft for condition: ${JSON.stringify(condition,null,2)}`);
  return null;
}

// Returns the right operand given a condition and data
const findRightOperand = (condition, data) => {
  if (isValid(condition.operandRight)) {
    return handleOperand(condition.operandRight, data);
  }

  if (isValid(condition.operand)) {
    return handleOperand(condition.operand, data);
  }

  if (isValid(condition.value)) {
    return condition.value;
  }

  NassauLog(`missing operandRight in ${JSON.stringify(condition,null,2)}`);
  return null
}

// Determine recursively if a condition is valid
export const isConditionValid = (condition, appData, _stack=[]) => {
  // push this condition on the stack
  // _stack.push(condition);

  // Check if data was passed in
  // If not generate a default data object
  if (!isValid(appData)) {
    appData = mygaSession.getCurrentAppData()
    if (isValid(appData)) {
      appData[STORAGE_KEYS.AgentInfo] = mygaSession.getAgentInfo();
      appData[STORAGE_KEYS.AppStateAbbr] = mygaSession.getMygaSessionValue(STORAGE_KEYS.AppStateAbbr, '');
    }
  }

  // Check that we have actual appData
  if (!isValid(appData)) {
    // Check if we want to fail on no data
    return !condition.hasOwnProperty('failOnNoData');
  }

  // Focus in on the desired selection of appData
  // If no selection can be made, appData is returned
  let selectedAppData = selectData(condition, appData);

  // Check that we have actual selectedAppData if not then return true
  if (!isValid(selectedAppData)) {
    // Check if we want to fail on no data
    return !condition.hasOwnProperty('failOnNoData');
  }

  // check for AND
  if (isValid(condition.AND) && condition.AND.length>0) {
    let filtered = condition.AND.filter(conditionPredicate);
    return andConditionIsValid(filtered, selectedAppData, _stack);
  }

  // check for OR
  if (isValid(condition.OR) && condition.OR.length>0) {
    let filtered = condition.OR.filter(conditionPredicate);
    return orConditionIsValid(filtered, selectedAppData, _stack);
  }

  // check for a named condition
  if (isValid(condition.name)) {
    // get the condition for this name
    let namedCondition = getNamedCondition(condition.name);
    if (isValid(namedCondition)) {
      // Get the target value if given. Otherwise assume true
      let value = isValid(condition.value) ? condition.value : true;
      return (isConditionValid(namedCondition, selectedAppData, _stack) === value);
    }
    // The named condition doesn't exist so ignore it by returning true
    return true;
  }

  // check for a page condition
  if (isValid(condition.page)) {
    let pagePrerequisite = getPageFlowFromProductType()[condition.page];
    if (isValid(pagePrerequisite) && isValid(pagePrerequisite.condition)) {
      return isConditionValid(pagePrerequisite.condition, selectedAppData, _stack);
    }
    // no prerequisite or condition so conditions are met
    return true;
  }

  // check FOR_ALL - applies the given condition to each object in the given section.
  // all conditions must be true for the result to be true.
  if (isValid(condition.FOR_ALL) && isValid(condition.FOR_ALL.condition)) {
    // selectedAppData must be an Array
    selectedAppData = selectData(condition.FOR_ALL, selectedAppData)
    if (!Array.isArray(selectedAppData)) {
      return false;
    }
    return andConditionIsValid(condition.FOR_ALL.condition, selectedAppData, _stack);
  }

  // check FOR_ANY - applies the given condition to each object in the given section.
  // for any object meets the condition we return true. otherwise if no object meets the condition return false.
  if (isValid(condition.FOR_ANY) && isValid(condition.FOR_ANY.condition)) {
    // selectedAppData must be an Array
    selectedAppData = selectData(condition.FOR_ANY, selectedAppData)
    if (!Array.isArray(selectedAppData)) {
      return false;
    }
    return orConditionIsValid(condition.FOR_ANY.condition, selectedAppData, _stack);
  }
  
  // check for a function
  // A function constraint expects an associated section.
  // If not supplied then return false since the condition is not met
  if (isValid(condition.function) && isFunction(condition.function)) {
    return isValid(selectedAppData) ? condition.function(selectedAppData) : false;
  }

  // assert: handle comparison operations
  
  // validate the operator
  if (!isValid(condition.operator)) {
    NassauLog(`missing operator for condition: ${JSON.stringify(condition,null,2)}`);
    return true;
  }

  // Configure the operands
  const operandLeft = findLeftOperand(condition, selectedAppData);
  const operandRight = findRightOperand(condition, selectedAppData);
  return evaluateOperands(operandLeft, operandRight, condition.operator);
};

export const validateCondition = (condition,data) => {
  if (isValid(condition) && isValid(data)) {
    // NassauLog(`validateCondition: condition - ${JSON.stringify(condition,null,2)}`);
    // NassauLog(`validateCondition: data - ${JSON.stringify(data,null,2)}`);
    // NassauLog(`validateCondition: value - ${isConditionValid(condition,data)}`);
    return isConditionValid(condition,data);
  }
  // If we get here we have no conditions
  return true;
}

const createAndValidateCondition = (name, value, appData) => {
  const condition = { name, value };
  return isConditionValid(condition, appData);
}

export const getConditionalValue = (conditionalValue,data) => {
  const isArray = _.isArray(conditionalValue);
  const isObj = _.isObject(conditionalValue);

  // If this is an array, return the first true condition
  if (isArray) {
    let result = null;
    for (let i=0; !result && i<conditionalValue.length; i++) {
      result = getConditionalValue(conditionalValue[i], data);
    }
    return result;
  }

  // If not an object, assume this is the desired value to return
  if (!isObj) return conditionalValue;

  // If condition, check if it is valid otherwise return null
  if (isValid(conditionalValue.condition)) {
    if (isConditionValid(conditionalValue.condition, data)) return getConditionalValue(conditionalValue.value);
    return null;
  }

  // check if this object is a condition value
  if (isValid(conditionalValue.conditionalValue)) return getConditionalValue(conditionalValue.conditionalValue, data);

  // check if this object is a value
  if (isValid(conditionalValue.value)) return conditionalValue.value;

  // nothing found worth returning
  return null;
}

/**
 * Return true if this is a PathB application.
 * Returns true if the application is D2C or referral and a
 * call must be scheduled for more information.
 * 2021.09.08.ADivelbiss - Use the definition from the named conditions.
 * @returns {boolean}
 */
export const isPathB = (appData) => createAndValidateCondition(CONDITION_NAMES.isPathB, true, appData);

/**
 * Return true if this is a PathB application.
 * Returns true if the application is D4A (i.e., a recommendation) and a
 * additional information is required for the agent to complete the app.
 * Essentially all of the conditions of pathB AND is a recommendation.
 * @param appData
 * @returns {boolean|boolean|*}
 */
export const isPathC = (appData) => createAndValidateCondition(CONDITION_NAMES.isPathC, true, appData);

/**
 * Returns true if the current application is an agent recommendation
 * @returns {boolean|boolean|*}
 */
export const isRecommendation = () => {
  const appData = {
    [STORAGE_KEYS.AgentInfo]: mygaSession.getAgentInfo()
  }
  let condition = createAndValidateCondition(CONDITION_NAMES.isRecommendation, true, appData);
  NassauLog(`isRecommendation: data: ${JSON.stringify(appData,null,2)}`)
  NassauLog(`isRecommendation: ${condition}`);
  return condition;
}

// 2021.08.10.ADivelbiss - centralized validation functions
export const isFinancesComplete = (appData) => createAndValidateCondition(PAGES.pageAppFinances, true, appData);
export const isKnockoutAny = (appData) => createAndValidateCondition(CONDITION_NAMES.knockoutAny, true, appData);
export const isKnockout = (appData) => createAndValidateCondition(CONDITION_NAMES.knockout, true, appData);
export const isKnockoutFinances = (appData) => createAndValidateCondition(CONDITION_NAMES.knockoutFinances, true, appData);
export const isPaymentOK = (appData) => createAndValidateCondition(CONDITION_NAMES.isPaymentOK, true, appData);
export const hasPayments = (appData) => createAndValidateCondition(CONDITION_NAMES.hasPayments, true, appData);
export const isPaymentScenarioValid = (appData) => createAndValidateCondition(CONDITION_NAMES.isPaymentScenarioValid, true, appData);
export const isReplacement = (appData) => createAndValidateCondition(CONDITION_NAMES.isReplacement, true, appData);
export const isCD = (appData) => createAndValidateCondition(CONDITION_NAMES.isCD, true, appData);
export const isReplacementOrCD = (appData) => createAndValidateCondition(CONDITION_NAMES.isReplacementOrCD, true, appData);
export const isUsingQualifiedFunds = (appData) => createAndValidateCondition(CONDITION_NAMES.isUsingQualifiedFunds, true, appData);
export const willStopPayments = (appData) => createAndValidateCondition(CONDITION_NAMES.willStopPayments, true, appData);
export const didConsent = (appData) => createAndValidateCondition(CONDITION_NAMES.didConsent, true, appData);
export const isPartyOK = (appData) => createAndValidateCondition(CONDITION_NAMES.isPartyOK, true, appData);
export const hasOwner = (appData) => createAndValidateCondition(CONDITION_NAMES.hasOwner, true, appData);
export const hasMinimumAllocations = (appData) => createAndValidateCondition(CONDITION_NAMES.hasMinimumAllocations, true, appData);
export const hasPrimaryBenes = (appData) => createAndValidateCondition(CONDITION_NAMES.hasPrimaryBenes, true, appData);
export const hasContingentBenes = (appData) => createAndValidateCondition(CONDITION_NAMES.hasContingentBenes, true, appData);
export const isPrimaryAllocationComplete = (appData) => createAndValidateCondition(CONDITION_NAMES.isPrimaryAllocationComplete, true, appData);
export const isContingentAllocationComplete = (appData) => createAndValidateCondition(CONDITION_NAMES.isContingentAllocationComplete, true, appData);

export const isTrue = (conditionName, appData) => createAndValidateCondition(conditionName, true, appData);
export const isIRA = (appData) => isTrue(CONDITION_NAMES.isIRA, appData);
export const hasJointOwner = (appData) => isTrue(CONDITION_NAMES.hasJointOwner, appData);
export const hasOwnerAsTrust = (appData) => isTrue(CONDITION_NAMES.hasOwnerAsTrust, appData);
export const hasOwnerAsIndividual = (appData) => isTrue(CONDITION_NAMES.hasOwnerAsIndividual, appData);
export const hasOwnerSameAsAnnuitant = (appData) => isTrue(CONDITION_NAMES.hasOwnerSameAsAnnuitant, appData);
export const hasAnnuitant = (appData) => isTrue(CONDITION_NAMES.hasAnnuitant, appData);
export const isEachPartyRelationshipOK = (appData) => isTrue(CONDITION_NAMES.isEachPartyRelationshipOK, appData);
export const hasJointAnnuitant = (appData) => isTrue(CONDITION_NAMES.hasJointAnnuitant, appData);
export const hasJointOwnerAsSpouse = (appData) => isTrue(CONDITION_NAMES.hasJointOwnerAsSpouse, appData);
export const hasSurvivingSpouseForPrimaryBene = (appData) => isTrue(CONDITION_NAMES.hasSurvivingSpouseForPrimaryBene, appData);
export const needsSurvivingSpouseForPrimaryBene = (appData) => isTrue(CONDITION_NAMES.needsSurvivingSpouseForPrimaryBene, appData);
export const isBeneValid = (appData) => isTrue(CONDITION_NAMES.isBeneValid, appData);
export const canAddParty = (appData) => isTrue(CONDITION_NAMES.canAddParty, appData);
export const canAddOwner = (appData) => isTrue(CONDITION_NAMES.canAddOwner, appData);
export const canAddAnnuitant = (appData) => isTrue(CONDITION_NAMES.canAddAnnuitant, appData);
export const canAddJointOwner = (appData) => isTrue(CONDITION_NAMES.canAddJointOwner, appData);
export const canAddJointAnnuitant = (appData) => isTrue(CONDITION_NAMES.canAddJointAnnuitant, appData);
export const isReferralCodeValid = (appData) => isTrue(CONDITION_NAMES.isReferralCodeValid, appData);
export const hasValidNetWorth = (appData) => isTrue(CONDITION_NAMES.hasValidNetWorth, appData);
export const areFinancialObjectivesOK = (appData) => isTrue(CONDITION_NAMES.areFinancialObjectivesOK,appData);
export const isFL = (appData) => isTrue(CONDITION_NAMES.isFL, appData);
export const hasOwnerInFlorida = (appData) => isTrue(CONDITION_NAMES.hasOwnerInFlorida, appData);
export const isRMD = (appData) => isTrue(CONDITION_NAMES.isRMD, appData);

// Centeralized reconciliation functions.
// A better method based on rule definitions in JSON is planned
// but for now this is a first cut
export const reconcileParties = (parties = mygaSession.getCurrentAppParties()) => {

  // return true if a change was made
  let nextParties = parties.map((party)=>{return {...party};});
  let owner = mygaSession.getCurrentAppOwner(nextParties);
  let jointOwner = mygaSession.getCurrentAppJointOwner(nextParties);
  let annuitant = mygaSession.getCurrentAppAnnuitant(nextParties);
  let jointAnnuitant = mygaSession.getCurrentAppJointAnnuitant(nextParties);
  
  // NassauLog(`reconcileParties: ${JSON.stringify(parties,null,2)}`);
  // NassauLog(`owner: ${JSON.stringify(owner,null,2)}`);
  // NassauLog(`annuitant: ${JSON.stringify(annuitant,null,2)}`);
  // NassauLog(`jointOwner: ${JSON.stringify(jointOwner,null,2)}`);
  // NassauLog(`jointAnnuitant: ${JSON.stringify(jointAnnuitant,null,2)}`);
  
  // SPECIAL RULES
  // if we are using qualified funds, there cannot be a joint owner.
  if (isUsingQualifiedFunds()) {
    if (isValid(jointOwner)) {
      // remove the jointOwner
      let index = nextParties.findIndex(p=>(p[PARTY.ownerType]===OWNER_TYPE.joint));
      if (index>=0) {
        nextParties.splice(index, 1);
        jointOwner = undefined;
      }
    }
  }
  
  // Handle owner
  if (isValid(owner)) {
    // Ensure that the owner email is same as principal
    owner[PARTY.email] = mygaSession.getPrincipal()[PRINCIPAL.email];
    
    // if FL was chosen at the start then set FL
    if (ENABLE_FLORIDA_FLOW) {
      if (isFL()) {
        owner[PARTY.addressState] = 'FL';
      } else if (owner[PARTY.addressState] === 'FL') {
        owner[PARTY.addressState] = '';
      }
    }
    
    // handle relationship to owner
    owner[PARTY.relationshipToOwner] = PARTY_RELATIONSHIPS.self;
    owner[PARTY.sameAsOwner] = 'Yes';
    
    // handle relationship to annuitant
    owner[PARTY.relationshipToAnnuitant] = !isValid(annuitant)
        ? PARTY_RELATIONSHIPS.none
        : (owner[PARTY.uid] === annuitant[PARTY.uid])
            ? PARTY_RELATIONSHIPS.self
            : getReciprocalRelationshipFor(annuitant[PARTY.relationshipToOwner]);
    owner[PARTY.sameAsAnnuitant] = (owner[PARTY.relationshipToAnnuitant] === PARTY_RELATIONSHIPS.self) ? 'Yes' : 'No';
  
    // handle relationship to jointOwner
    owner[PARTY.relationshipToJointOwner] = !isValid(jointOwner)
        ? PARTY_RELATIONSHIPS.none
        : (owner[PARTY.uid] === jointOwner[PARTY.uid])
            ? PARTY_RELATIONSHIPS.self
            : (owner[PARTY.entityType] === ENTITY_TYPE.individual)
                ? PARTY_RELATIONSHIPS.spouse  // if the owner is an individual jointOwner must be spouse
                : getReciprocalRelationshipFor(jointOwner[PARTY.relationshipToOwner]);
    owner[PARTY.sameAsJointOwner] = (owner[PARTY.relationshipToJointOwner] === PARTY_RELATIONSHIPS.self) ? 'Yes' : 'No';
    
    // handle relationship to jointAnnuitant
    owner[PARTY.relationshipToJointAnnuitant] = !isValid(jointAnnuitant)
        ? PARTY_RELATIONSHIPS.none
        : (owner[PARTY.uid] === jointAnnuitant[PARTY.uid])
            ? PARTY_RELATIONSHIPS.self
            : (isValid(jointOwner)) && (jointOwner[PARTY.uid] === jointAnnuitant[PARTY.uid])
              ? getReciprocalRelationshipFor(owner[PARTY.relationshipToJointOwner])
              : getReciprocalRelationshipFor(jointAnnuitant[PARTY.relationshipToOwner]);
    owner[PARTY.sameAsJointAnnuitant] = (owner[PARTY.relationshipToJointAnnuitant] === PARTY_RELATIONSHIPS.self) ? 'Yes' : 'No';
    
  }
  
  // handle annuitant
  if (isValid(annuitant)) {
    
    // handle relationship to the owner
    annuitant[PARTY.relationshipToOwner] = !isValid(owner)
        ? PARTY_RELATIONSHIPS.none
        : getReciprocalRelationshipFor(owner[PARTY.relationshipToAnnuitant]);
    annuitant[PARTY.sameAsOwner] = (annuitant[PARTY.relationshipToOwner] === PARTY_RELATIONSHIPS.self) ? 'Yes' : 'No';
    
    // handle relationship to annuitant
    annuitant[PARTY.relationshipToAnnuitant] = PARTY_RELATIONSHIPS.self;
    annuitant[PARTY.sameAsAnnuitant] = 'Yes';
  
    // handle relationship to jointOwner
    annuitant[PARTY.relationshipToJointOwner] = !isValid(jointOwner)
        ? PARTY_RELATIONSHIPS.none
        : (annuitant[PARTY.uid] === jointOwner[PARTY.uid])
            ? PARTY_RELATIONSHIPS.self
            : (isValid(owner) && (annuitant[PARTY.uid] === owner[PARTY.uid]))
                ? owner[PARTY.relationshipToJointOwner] // annuitant and owner are the same so use owner
                : getReciprocalRelationshipFor(jointOwner[PARTY.relationshipToAnnuitant]);
    annuitant[PARTY.sameAsJointOwner] = (annuitant[PARTY.relationshipToJointOwner] === PARTY_RELATIONSHIPS.self) ? 'Yes' : 'No';
  
    // handle relationship to jointAnnuitant
    annuitant[PARTY.relationshipToJointAnnuitant] = !isValid(jointAnnuitant)
        ? PARTY_RELATIONSHIPS.none
        : (annuitant[PARTY.uid] === jointAnnuitant[PARTY.uid])
            ? PARTY_RELATIONSHIPS.self
            : (isValid(owner) && (annuitant[PARTY.uid] === owner[PARTY.uid]))
                ? owner[PARTY.relationshipToJointAnnuitant] // annuitant and owner are the same so use owner
                : getReciprocalRelationshipFor(jointAnnuitant[PARTY.relationshipToAnnuitant]);
    annuitant[PARTY.sameAsJointAnnuitant] = (annuitant[PARTY.relationshipToJointAnnuitant] === PARTY_RELATIONSHIPS.self) ? 'Yes' : 'No';
  
  } // annuitant
  
  
  // handle joint owner
  if (isValid(jointOwner)) {
    // handle relationship to the owner
    jointOwner[PARTY.relationshipToOwner] = !isValid(owner)
        ? PARTY_RELATIONSHIPS.none
        : getReciprocalRelationshipFor(owner[PARTY.relationshipToJointOwner]);
    jointOwner[PARTY.sameAsOwner] = (jointOwner[PARTY.relationshipToOwner] === PARTY_RELATIONSHIPS.self) ? 'Yes' : 'No';
    
    // handle relationship to annuitant
    jointOwner[PARTY.relationshipToAnnuitant] = !isValid(annuitant)
        ? PARTY_RELATIONSHIPS.none
        : getReciprocalRelationshipFor(annuitant[PARTY.relationshipToJointOwner]);
    jointOwner[PARTY.sameAsAnnuitant] = (jointOwner[PARTY.relationshipToAnnuitant] === PARTY_RELATIONSHIPS.self) ? 'Yes' : 'No';
  
    // handle realationship to jointOwner
    jointOwner[PARTY.relationshipToJointOwner] = PARTY_RELATIONSHIPS.self;
    jointOwner[PARTY.sameAsJointOwner] = 'Yes';
  
    // handle relationship to jointAnnuitant
    jointOwner[PARTY.relationshipToJointAnnuitant] = !isValid(jointAnnuitant)
        ? PARTY_RELATIONSHIPS.none
        : (jointOwner[PARTY.uid] === jointAnnuitant[PARTY.uid])
            ? PARTY_RELATIONSHIPS.self
            : jointOwner[PARTY.relationshipToJointAnnuitant];
    jointOwner[PARTY.sameAsJointAnnuitant] = (jointOwner[PARTY.relationshipToJointAnnuitant] === PARTY_RELATIONSHIPS.self) ? 'Yes' : 'No';
  } // handle joint owner
  
  // Handle joint annuitant
  if (isValid(jointAnnuitant)) {
    // handle relationship to the owner
    jointAnnuitant[PARTY.relationshipToOwner] = !isValid(owner)
        ? PARTY_RELATIONSHIPS.none
        : getReciprocalRelationshipFor(owner[PARTY.relationshipToJointAnnuitant]);
    jointAnnuitant[PARTY.sameAsOwner] = (jointAnnuitant[PARTY.relationshipToOwner] === PARTY_RELATIONSHIPS.self) ? 'Yes' : 'No';
  
    // handle relationship to annuitant
    jointAnnuitant[PARTY.relationshipToAnnuitant] = !isValid(annuitant)
        ? PARTY_RELATIONSHIPS.none
        : getReciprocalRelationshipFor(annuitant[PARTY.relationshipToJointAnnuitant]);
    jointAnnuitant[PARTY.sameAsAnnuitant] = (jointAnnuitant[PARTY.relationshipToAnnuitant] === PARTY_RELATIONSHIPS.self) ? 'Yes' : 'No';
    
    // handle relationship to joint owner
    jointAnnuitant[PARTY.relationshipToJointOwner] = !isValid(jointOwner)
        ? PARTY_RELATIONSHIPS.none
        : getReciprocalRelationshipFor(jointOwner[PARTY.relationshipToJointAnnuitant]);
    jointAnnuitant[PARTY.sameAsJointOwner] = (jointAnnuitant[PARTY.relationshipToJointOwner] === PARTY_RELATIONSHIPS.self) ? 'Yes' : 'No';

    // handle relationship to joint annuitant
    jointAnnuitant[PARTY.relationshipToJointAnnuitant] = PARTY_RELATIONSHIPS.self;
    jointAnnuitant[PARTY.sameAsJointAnnuitant] = 'Yes';
    
  } // Handle joint annuitant


  // handle changes in the parties
  return isEqual(nextParties,parties) ? null : nextParties;
}

// 2022.01.11.ADivelbiss - udpatd to match the reconcileParties method.
export const reconcileBenes = (benes = mygaSession.getCurrentAppBenes()) => {
  
  // Make a copy of each bene
  let nextBenes = benes.map((b)=>{return {...b};});
  
  // 2022.01.11.ADivelbiss - updated for demo this date.
  if (needsSurvivingSpouseForPrimaryBene()) {
    NassauLog('reconcileBenes: needsSurvivingSpouseForPrimaryBene')
    if (!hasSurvivingSpouseForPrimaryBene()) {
      NassauLog('reconcileBenes: !hasSurvivingSpouseForPrimaryBene')
      // add the surviving spouse
      let survivor = createPrimaryBeneficiary();
      survivor[BENEFICIARY.uid] = BENEFICIARY_SURVIVOR_UID;
      survivor[BENEFICIARY.firstName] = BENEFICIARY_SURVIVOR_FIRST_NAME;
      survivor[BENEFICIARY.lastName] = BENEFICIARY_SURVIVOR_LAST_NAME;
      survivor[BENEFICIARY.allocation] = 100;
      survivor[BENEFICIARY.relationshipToOwner] = PARTY_RELATIONSHIPS.spouse;
      //NassauLog(`Party-Relationship: ${PARTY_RELATIONSHIPS.spouse}\n\nThe Relationship-To-Owner: ${survivor[BENEFICIARY.relationshipToOwner]}`);
      nextBenes.push(survivor);
    }
    // switch to contingent any benes NOT the survivor
    let toUpdate = nextBenes
        .filter(bene=>bene[BENEFICIARY.beneficiaryType]===BENEFICIARY_TYPE.primary)
        .map(bene=>bene[BENEFICIARY.uid])
        .filter(uid=>uid!==BENEFICIARY_SURVIVOR_UID);
    toUpdate.forEach(uid=>{
      let index = nextBenes.findIndex(b=>(b[BENEFICIARY.uid]===uid));
      if (index>=0) {
        nextBenes[index][BENEFICIARY.beneficiaryType] = BENEFICIARY_TYPE.contingent;
        // nextBenes.splice(index,1); // keep if we want to remove the bene
      }
    });
  } else if (hasSurvivingSpouseForPrimaryBene()) {
    NassauLog('reconcileBenes: hasSurvivingSpouseForPrimaryBene')
    // remove the surviving spouse
    let index = nextBenes.findIndex(b=>(b[BENEFICIARY.uid]===BENEFICIARY_SURVIVOR_UID));
    if (index>=0) {
      nextBenes.splice(index,1);
    }
  }
  
  // if the owner is a trust AND there is no primary bene, then add the owner as primary bene
  if (hasOwnerAsTrust() && !hasPrimaryBenes({[APP_SECTION.benes]: nextBenes})) {
    NassauLog('reconcileBenes: hasOwnerAsTrust');
    let bene = createPrimaryBeneficiaryFromParty(mygaSession.getCurrentAppOwner());
    
    nextBenes.push(bene);
  }
  
  // NassauLog("Entering Reconcile BENES ---------------");
  // if (!hasJointOwnerAsSpouse()) {
  //   // Remove Surviving Spouse
  // }
  // //Is there a joint Owner?
  // //  - retrieve joint Owner info
  // /*if (hasJointOwner()) {
  //   NassauLog("We have entered Reconcile Benes \n\n\n")
  //   let nextParties = mygaSession.getCurrentAppParties().map((party) => {
  //     return {...party};
  //   });
  //   let jointOwner = mygaSession.getCurrentAppJointOwner(nextParties);
  //   // CHECK UID of Joint Owner, see if it is in currentAppBenes[].
  //   let joUID = jointOwner[PARTY.uid];
  //
  //     // If its in currentAppBenes -> Skip!
  //     // If not -> Add!
  //   NassauLog(`This is our Joint Owner: ${JSON.stringify(jointOwner,null,2)}`)
  //   let newBene = createPrimaryBeneficiaryFromParty(jointOwner);
  //   NassauLog(`New Bene: ${JSON.stringify(newBene,null,2)}`);
  //   // Set this data as the primary bene
  //   mygaSession.addCurrentAppBene(newBene);
  // }*/
  // if (!hasSurvivingSpouseForPrimaryBene()) {
  //   NassauLog("We are here");
  //   // Check to see if there is a surviving spouse, if not then add.
  //   mygaSession.addCurrentAppSurvivingSpouse();
  //   // Find the bene that has surviving spouse uid
  //   //    MAKE A hasSurvivingSpouseForPrimaryBene Condition
  //   //        * Get the benes, check to see if one of them has the survivor uid
  //   //  -> If found : Nothing happens
  //   //  -> If not found: Add surviving bene
  //   //      * mygaSession.addCurrentAppBene()
  //
  //
  // }
  // //Retrieve beneficiaries
  // //  - Do we have any primaries?
  // //  - Add joint Owner as the ONLY primary bene
  // //
  //
  
  // handle changes in the parties
  return isEqual(nextBenes,benes) ? null : nextBenes;
  
}

export const reconcilePayors = () => {
  let result = false;
  // let paymentsArray = APPLICATION.payments;
  let parties = mygaSession.getCurrentAppParties();
  if (parties.length === 0){
    return result;
  }
  let ownerUID = mygaSession.getCurrentAppOwner()[PARTY.uid];
  let payments = mygaSession.getCurrentAppPayments();
  let paymentsData = Object.values(payments);

  paymentsData.forEach((product) => {
    product.forEach((payment) => {
      if(parties.filter(p=>(p[PARTY.uid] === payment.payorUid)).length === 0){
        payment.payorUid = ownerUID;
        result = true;
      }
    })
  })
  if (result) {
    mygaSession.setCurrentAppSectionData(APP_SECTION.payments, payments);
  }
  return result;
}

export const reconcilePaymentsForProducts = (products = mygaSession.getCurrentAppProductDTOs()) => {
  let result = null;
  // check to see if we have any payments
  if (hasPayments({products: products})) {
    const nextProducts = [...products];
    nextProducts.forEach((productDTO)=>{
      let nextPayments = [...productDTO[MM_PRODUCT_DTO.payments]];
      if (isReplacementOrCD()) {
        // there must be one 1035 payment
        nextPayments = [makePayment(MM_PAYMENT_TYPE.exchange1035,productDTO[MM_PRODUCT_DTO.premium])];
      } else {
        // payments can't be 1035
        nextPayments = nextPayments.filter(payment=>payment[MM_PAYMENT.paymentType]!==MM_PAYMENT_TYPE.exchange1035);
      }
      if (!isEqual(nextPayments,productDTO[MM_PRODUCT_DTO.payments])) {
        productDTO[MM_PRODUCT_DTO.payments] = nextPayments;
        result = nextProducts;
      }
    });
  } else if (isReplacementOrCD()) {
    const nextProducts = [...products];
    // there must be one 1035 payment for each
    nextProducts.forEach((productDTO)=> {
      productDTO[MM_PRODUCT_DTO.payments] = [makePayment(MM_PAYMENT_TYPE.exchange1035, productDTO[MM_PRODUCT_DTO.premium])];
    });
    result = nextProducts;
  }
  return result;
}

/**
 * Returns an object that contains the status of each named condition
 */
export const getValidationReport = () => {
  return [...VALIDATION_REPORT]
      .sort()
      .reduce((prev,name)=>{
        let next = {};
        Object.assign(next,prev);
        if (name!==undefined) {
          next[name] = createAndValidateCondition(name, true);
        }
        return next;
      },{});
}